/**
 * Engine hours: how they are written, read and compared. Shared by the server and the forms, so
 * both accept and refuse exactly the same values.
 *
 * - A value is a number of hours with up to two decimals, written with a comma or a point
 *   ("1234,5", "1234.5", "1234"). Nothing else is accepted: no exponent, no thousands separator,
 *   no mix of separators, and no silent rounding of more than two decimals.
 * - An empty value means unknown, never zero.
 * - The meter keeps counting while the engine runs, so a new reading can never be lower than the
 *   one before, and cannot grow by more hours than have passed (plus a tolerance) without a reason.
 */

export const MAX_HOURS = 1_000_000;
export const HOURS_DECIMALS = 2;
/** How many hours more than the time that has passed a reading may grow before it needs a reason. */
export const HOURS_TOLERANCE = 0.1;

export type HoursParse = { ok: true; value: number } | { ok: false; reason: "empty" | "format" | "decimals" | "range" };

/** Read the text of a field: "1234,5" gives 1234.5. */
export function parseHours(input: string): HoursParse {
  const text = input.trim();
  if (text === "") return { ok: false, reason: "empty" };
  const m = /^(\d+)(?:[.,](\d+))?$/.exec(text);
  if (!m) return { ok: false, reason: "format" };
  if (m[2] !== undefined && m[2].length > HOURS_DECIMALS) return { ok: false, reason: "decimals" };
  const value = Number(m[2] !== undefined ? `${m[1]}.${m[2]}` : m[1]);
  if (!Number.isFinite(value) || value > MAX_HOURS) return { ok: false, reason: "range" };
  return { ok: true, value };
}

/** Is this number a valid number of hours (finite, not negative, within the limit, at most two decimals)? */
export function isValidHours(n: unknown): n is number {
  if (typeof n !== "number" || !Number.isFinite(n) || n < 0 || n > MAX_HOURS) return false;
  return Math.abs(Math.round(n * 100) - n * 100) < 1e-6;
}

/** Hours as they are shown: the shortest form that loses nothing, with a comma ("1234,5"). */
export function formatHours(n: number): string {
  return String(Math.round(n * 100) / 100).replace(".", ",");
}

export interface HoursComparison {
  /** hours gained since the earlier reading (negative when lower) */
  increase: number;
  /** hours that have passed between the two readings */
  elapsedHours: number;
  kind: "ok" | "lower" | "jump";
}

/**
 * Compare a reading with an earlier one. The same value is fine (the engine may have stood still);
 * a lower value is a correction case; a gain larger than the time that has passed (plus the
 * tolerance) is a jump that needs a reason.
 */
export function compareReadings(earlier: { total: number; at: string }, later: { total: number; at: string }): HoursComparison {
  const increase = Math.round((later.total - earlier.total) * 100) / 100;
  const elapsedHours = Math.max(0, (Date.parse(later.at) - Date.parse(earlier.at)) / 3_600_000);
  if (increase < 0) return { increase, elapsedHours, kind: "lower" };
  return { increase, elapsedHours, kind: increase > elapsedHours + HOURS_TOLERANCE ? "jump" : "ok" };
}
